import { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery as useConvexQuery, useAction } from "convex/react";
import { api } from "../../api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";

import { Input } from "@/components/ui/input";
import { AppIcon } from "./AppIcon";
import { Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { AppMode } from "@/types/convex";

interface DeviceApp {
  bundleId: string;
  name?: string;
  iconUrl?: string;
  version?: string;
  status: "allowed" | "blocked" | "unknown";
}

interface InstalledAppsReviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deviceId: string;
  pendingMode: AppMode;
  onConfirm: (allowedBundleIds: string[], blockedBundleIds: string[]) => void;
  onCancel: () => void;
  mode?: "modeSwitch" | "addToWhitelist";
}

export const InstalledAppsReviewModal = ({
  open,
  onOpenChange,
  deviceId,
  pendingMode,
  onConfirm,
  onCancel,
  mode = "modeSwitch",
}: InstalledAppsReviewModalProps) => {
  const [appToggles, setAppToggles] = useState<Map<string, boolean>>(new Map());
  const [apps, setApps] = useState<DeviceApp[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const deviceData = useConvexQuery(
    api.devices.get.details,
    deviceId ? { device: deviceId as any } : "skip"
  );
  const deviceSerial = deviceData?.device?.serial;

  const fetchAppsAction = useAction(api.apps.list.allOnDeviceWithStatus);

  const fetchApps = useCallback(async () => {
    if (!deviceSerial) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchAppsAction({ serial: deviceSerial });
      const fetchedApps: DeviceApp[] = result || [];
      setApps(fetchedApps);

      // In addToWhitelist mode, start all toggles off (user picks what to add)
      // In modeSwitch mode, start all toggles on
      const toggleMap = new Map<string, boolean>();
      fetchedApps.forEach((app) => {
        toggleMap.set(app.bundleId, mode !== "addToWhitelist");
      });
      setAppToggles(toggleMap);
    } catch (err) {
      console.error("Error fetching apps for review:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch apps");
    } finally {
      setIsLoading(false);
    }
  }, [deviceSerial, fetchAppsAction, mode]);

  // Fetch apps when modal opens and serial is available
  useEffect(() => {
    if (open && deviceSerial) {
      fetchApps();
    }
  }, [open, deviceSerial, fetchApps]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setError(null);
    }
  }, [open]);

  const handleToggle = (bundleId: string) => {
    setAppToggles((prev) => {
      const next = new Map(prev);
      next.set(bundleId, !prev.get(bundleId));
      return next;
    });
  };

  const allowedCount = useMemo(
    () => Array.from(appToggles.values()).filter(Boolean).length,
    [appToggles]
  );

  const filteredApps = useMemo(() => {
    const HIDDEN_BUNDLE_IDS = ["com.apple.mobilesafari", "com.google.chrome.ios", "com.apple.appstored", "com.apple.appstore", "com.apple.store.itunes", "com.shomri.bluejay"];
    let result = apps.filter((app) => !HIDDEN_BUNDLE_IDS.includes(app.bundleId.toLowerCase()));
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (app) =>
          (app.name || "").toLowerCase().includes(term) ||
          app.bundleId.toLowerCase().includes(term)
      );
    }
    return result;
  }, [apps, searchTerm]);

  const handleConfirm = () => {
    const allowed: string[] = [];
    const blocked: string[] = [];
    appToggles.forEach((isAllowed, bundleId) => {
      if (isAllowed) allowed.push(bundleId);
      else blocked.push(bundleId);
    });
    onConfirm(allowed, blocked);
    if (mode === "addToWhitelist") {
      toast.success(`${allowed.length} app${allowed.length !== 1 ? "s" : ""} added to the whitelist.`);
    } else {
      toast.info(
        `Mode changed to Whitelist. ${allowed.length} apps allowed, ${blocked.length} blocked.`
      );
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const modeLabel = pendingMode === "whitelist" ? "Whitelist" : "Categories";
  const isAddMode = mode === "addToWhitelist";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            {isAddMode ? "Add Apps from Device" : "Review Installed Apps"}
          </DialogTitle>
          <DialogDescription>
            {isAddMode
              ? "Select apps currently installed on this device to add to the whitelist."
              : `You're switching to ${modeLabel} mode. Review the apps currently installed on this device and choose which to allow.`}
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search apps..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* App List */}
        <ScrollArea className="h-[50vh] border rounded-md">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading apps...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-4">
              <p className="text-destructive mb-2">Failed to load apps</p>
              <p className="text-sm text-muted-foreground">{error}</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={fetchApps}>
                Retry
              </Button>
            </div>
          ) : filteredApps.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              {searchTerm ? "No apps match your search" : "No installed apps found"}
            </div>
          ) : (
            <div className="divide-y">
              {filteredApps.map((app) => {
                const isAllowed = appToggles.get(app.bundleId) ?? false;
                return (
                  <div
                    key={app.bundleId}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50"
                  >
                    <AppIcon
                      app_icon_url={app.iconUrl}
                      app_name={app.name}
                      size="sm"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {app.name || app.bundleId}
                      </p>
                      {app.name && (
                        <p className="text-xs text-muted-foreground truncate">
                          {app.bundleId}
                        </p>
                      )}
                    </div>
                    <Switch
                      checked={isAllowed}
                      onCheckedChange={() => handleToggle(app.bundleId)}
                      className={isAllowed ? "data-[state=checked]:bg-green-500" : "data-[state=unchecked]:bg-red-400"}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <p className="text-sm text-muted-foreground">
            {isAddMode ? `${allowedCount} selected` : `${allowedCount} allowed`}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={isLoading || (isAddMode && allowedCount === 0)}>
              {isAddMode ? `Add ${allowedCount > 0 ? allowedCount : ""} App${allowedCount !== 1 ? "s" : ""}` : "Done"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
